import { pluralize } from "@prismicio/editor-support/String"
import {
  BackgroundIcon,
  Badge,
  Box,
  Card,
  CardContent,
  CardHeader,
  CardHeaderTitle,
  ProgressBar,
  Separator,
  Skeleton,
  Text,
} from "@prismicio/editor-ui"
import type { ReactNode } from "react"

import { useSubscriptionQuery } from "../billing/useSubscription"
import { canAddLocale } from "./localeUtils"
import { useRepositoryLocalesQuery } from "./useRepositoryLocales"

export function LocalesUsageCard() {
  const { data: repositoryLocales } = useRepositoryLocalesQuery()
  const { data: subscription } = useSubscriptionQuery()

  const localeCount = repositoryLocales.length
  const { locales: localesLimit } = subscription.limits

  return (
    <LocalesUsageCardTemplate
      title={
        <CardHeaderTitle variant="h4">
          {localeCount}
          {localesLimit === "unlimited" ? "" : `/${localesLimit}`}{" "}
          {pluralize(localeCount, "locale")} created in this repository
        </CardHeaderTitle>
      }
      badge={<Badge color="purple" title={`${subscription.nickname} plan`} />}
      progressBar={
        canAddLocale(localesLimit, localeCount) &&
        localesLimit !== "unlimited" ? (
          <ProgressBar
            backgroundColor="purple5"
            color="purple8"
            height="large"
            max={localesLimit}
            value={localeCount}
            sx={{ marginBottom: 8 }}
          />
        ) : undefined
      }
      description={
        <Text color="grey11">
          {canAddLocale(localesLimit, localeCount)
            ? "Make your website relevant across markets by incorporating new locales with Prismic. Begin by adding the languages you wish to support, and then proceed to localize your content. Prismic allows for the localization of all your content types and assets, including rich text, responsive images, geo-points, and your URLs."
            : "You have reached the limit of locales available with your current plan.\nTo add more locales upgrade your plan."}
        </Text>
      }
    />
  )
}

interface LocalesUsageCardTemplateProps {
  title: ReactNode
  badge: ReactNode
  progressBar: ReactNode
  description: ReactNode
}

function LocalesUsageCardTemplate(props: LocalesUsageCardTemplateProps) {
  const { title, badge, progressBar, description } = props
  return (
    <Card color="purple2" padding={12} variant="highlighted">
      <CardHeader>
        <BackgroundIcon color="purple" name="language" size="extraSmall" />
        {title}
        {badge}
      </CardHeader>
      <CardContent gap={12}>
        {progressBar}
        <Separator color="purple" style="dashed" />
        <Box flexDirection="column" padding={{ bottom: 8, inline: 8 }}>
          {description}
        </Box>
      </CardContent>
    </Card>
  )
}

export function LocalesUsageCardSkeleton() {
  return (
    <LocalesUsageCardTemplate
      title={
        <Box flexGrow={1}>
          <Skeleton width={276} height={24} color="purple5" />
        </Box>
      }
      badge={<Skeleton width={74} height={28} color="purple5" />}
      progressBar={
        <Skeleton height={9} color="purple5" sx={{ marginBottom: 8 }} />
      }
      description={
        <>
          <Skeleton width={444} height={16} sx={{ marginBlock: 4 }} />
          <Skeleton width={255} height={16} sx={{ marginBlock: 4 }} />
        </>
      }
    />
  )
}
